import CtaButtonGroup, { CtaActionType } from "@/components/molecules/CtaButtonGroup";
import {
  PlatformTabItem,
  PlatformTabsProps,
} from "@/shared/data/home.v2.data";
import {
  faBookOpen as faBookOpenRegular,
  faCalendar as faCalendarRegular,
  faFile as faFileRegular,
  faUserHeadset as faUserHeadsetRegular,
  faUsers as faUsersRegular,
  faVideo as faVideoRegular,
  faWandMagicSparkles as faWandMagicSparklesRegular,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faBookOpen as faBookOpenSolid,
  faCalendar as faCalendarSolid,
  faFile as faFileSolid,
  faUserHeadset as faUserHeadsetSolid,
  faUsers as faUsersSolid,
  faVideo as faVideoSolid,
  faWandMagicSparkles as faWandMagicSparklesSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import SectionHeader from "./shared/SectionHeader";

/**
 * How long (ms) each tab stays active before the auto-rotation advances
 * to the next one. Matches the duration of the progress-fill animation
 * on the active tab so the fill reaches 100% exactly as the tab flips.
 */
const AUTO_ADVANCE_MS = 5000;

/**
 * Tab-id → { regular, solid } icon pair. Resting tabs render the outline
 * (Pro Regular) glyph; the active tab swaps to the filled (Pro Solid)
 * glyph for a clear selected-state contrast.
 */
const TAB_ICONS: Record<
  string,
  {
    regular: typeof faBookOpenRegular;
    solid: typeof faBookOpenSolid;
  }
> = {
  courses: { regular: faBookOpenRegular, solid: faBookOpenSolid },
  memberships: { regular: faCalendarRegular, solid: faCalendarSolid },
  communities: { regular: faUsersRegular, solid: faUsersSolid },
  coaching: { regular: faUserHeadsetRegular, solid: faUserHeadsetSolid },
  webinars: { regular: faVideoRegular, solid: faVideoSolid },
  "digital-products": { regular: faFileRegular, solid: faFileSolid },
  "ai-avatars": {
    regular: faWandMagicSparklesRegular,
    solid: faWandMagicSparklesSolid,
  },
};

/**
 * PlatformTabsSection — "Everything Your Audience Needs, In One Platform"
 *
 * Dark section with:
 *  - lime "CREATE" eyebrow
 *  - two-tone headline (grey + white)
 *  - icon + label tab bar with lime underline for the active tab
 *  - featured card with product-specific title, description, CTAs and visual
 */

const TabVisualPlaceholder = ({ kind }: { kind: string }) => {
  // Lightweight gradient-backed placeholders per visual kind.
  // These can be replaced 1-by-1 with real components as assets land.
  return (
    <div className="relative isolate w-full overflow-hidden rounded-2xl border border-white/10 bg-[#1a1a22]">
      <div className="aspect-[16/10] w-full bg-gradient-to-br from-[#2a2a33] via-[#1a1a22] to-[#11111a]" />
      <div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-t from-[#ff7fb3] via-[#ff9a7a]/40 to-transparent opacity-80" />
      <div className="absolute bottom-3 right-3 rounded-full bg-white/10 px-2 py-1 font-inter text-[10px] uppercase tracking-wider text-white/60 backdrop-blur-sm">
        {kind}
      </div>
    </div>
  );
};

const TabVisual = ({
  image,
  imageMobile,
  alt,
  kind,
}: {
  image?: string;
  imageMobile?: string;
  alt: string;
  kind: string;
}) => {
  if (!image && !imageMobile) {
    return <TabVisualPlaceholder kind={kind} />;
  }
  return (
    <div className="relative w-full overflow-hidden rounded-2xl">
      {image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={image}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={classNames(
            "h-auto w-full object-contain",
            imageMobile ? "hidden md:block" : "block"
          )}
        />
      ) : null}
      {imageMobile ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageMobile}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={classNames(
            "h-auto w-full object-contain",
            image ? "block md:hidden" : "block"
          )}
        />
      ) : null}
    </div>
  );
};

const PlatformTabsSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  subtitle,
  primaryCta,
  secondaryCta,
  tabs,
}: PlatformTabsProps) => {
  const [activeId, setActiveId] = useState(tabs[0]?.id);
  // Bumped each time the active tab changes — used as a CSS animation
  // key so the progress bar restarts from 0 on every tab switch.
  const [progressKey, setProgressKey] = useState(0);
  // Auto-advance is paused until the section first scrolls into view, so
  // users landing on the first tab actually see the rotation begin from
  // Courses rather than mid-way through when they scroll down.
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const active: PlatformTabItem | undefined =
    tabs.find((t) => t.id === activeId) ?? tabs[0];

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const markEntered = () => {
      setHasEnteredView(true);
      setProgressKey((k) => k + 1);
    };
    // Pure geometry check — works even in environments where
    // IntersectionObserver callbacks are suppressed (some headless
    // browsers / preview harnesses).
    const isInViewport = () => {
      const r = node.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };
    if (isInViewport()) {
      markEntered();
      return;
    }
    const onScroll = () => {
      if (isInViewport()) {
        markEntered();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-advance: every AUTO_ADVANCE_MS move to the next tab. The timer
  // is recreated whenever the active tab changes (including manual clicks),
  // so clicking a tab resets the 5-second countdown rather than advancing
  // immediately from the previous schedule.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!tabs.length) return;
    if (!hasEnteredView) return;
    timerRef.current = setTimeout(() => {
      const currentIdx = tabs.findIndex((t) => t.id === activeId);
      const nextIdx = (currentIdx + 1) % tabs.length;
      setActiveId(tabs[nextIdx].id);
      setProgressKey((k) => k + 1);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeId, tabs, hasEnteredView]);

  const handleTabClick = (id: string) => {
    setActiveId(id);
    setProgressKey((k) => k + 1);
  };

  return (
    <section ref={sectionRef} className="w-full bg-black text-white">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          subtitle={subtitle}
          variant="dark-lime"
          align="center"
        />

        {/* Tab picker — two layouts:
            • Mobile (<md): grid of chip-buttons so ALL 7 products are
              visible in the first paint and the active chip is always
              on-screen as the auto-rotation moves between tabs.
            • Desktop (md+): single-row flex bar with lime underline +
              progress rail (original design). */}
        <div className="mt-10 md:mt-16">
          {/* Mobile: all-visible chip grid */}
          <div className="grid grid-cols-4 gap-2 md:hidden">
            {tabs.map((tab) => {
              const isActive = tab.id === activeId;
              const iconPair = TAB_ICONS[tab.id];
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabClick(tab.id)}
                  aria-pressed={isActive}
                  className={classNames(
                    "relative flex flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border px-1 py-2 font-inter text-[11px] font-medium transition-colors",
                    isActive
                      ? "border-brandSecondary bg-brandSecondary/15 text-brandSecondary"
                      : "border-white/10 bg-white/[0.04] text-white/75"
                  )}
                >
                  {iconPair ? (
                    <FontAwesomeIcon
                      icon={isActive ? iconPair.solid : iconPair.regular}
                      className="text-[14px]"
                    />
                  ) : null}
                  <span className="text-center leading-tight">{tab.label}</span>
                  {/* Progress fill — bottom strip on the active chip so the
                      user sees the auto-advance countdown even on mobile. */}
                  {isActive ? (
                    <span
                      key={progressKey}
                      aria-hidden="true"
                      className="platform-tab-progress-fill absolute inset-x-0 bottom-0 block h-[2px] bg-brandSecondary"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Desktop: original single-row tab bar with underline progress */}
          <div className="hidden md:block">
            <div className="mx-auto flex w-full items-end justify-center gap-0">
              {tabs.map((tab) => {
                const isActive = tab.id === activeId;
                const iconPair = TAB_ICONS[tab.id];
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabClick(tab.id)}
                    className={classNames(
                      "relative flex shrink-0 items-center gap-2 px-3 pb-3 pt-2 font-inter text-[14px] transition-colors sm:px-4 sm:text-[15px] md:px-6",
                      isActive
                        ? "text-brandSecondary"
                        : "text-white/75 hover:text-white"
                    )}
                  >
                    {iconPair ? (
                      <FontAwesomeIcon
                        icon={isActive ? iconPair.solid : iconPair.regular}
                        className="text-[16px]"
                      />
                    ) : null}
                    <span className="whitespace-nowrap font-medium">{tab.label}</span>
                    <span className="pointer-events-none absolute inset-x-2 bottom-0 h-[2px] overflow-hidden rounded-full bg-white/10">
                      {isActive ? (
                        <span
                          key={progressKey}
                          className="platform-tab-progress-fill block h-full w-full rounded-full bg-brandSecondary"
                        />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="h-px w-full bg-white/10" />
          </div>
        </div>

        {/* Featured card */}
        {active ? (
          <div className="relative mx-auto mt-10 max-w-6xl overflow-hidden rounded-[32px] bg-[#0e0e14] md:mt-12">
            {/* Bottom pink/orange gradient wash */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#ff5fa2] via-[#ff9a7a]/40 to-transparent" />

            <div className="relative grid gap-8 p-6 sm:p-8 md:p-12 lg:p-16">
              {/* Text block spans the same width as the visual below it —
                  previously capped at max-w-3xl which forced multi-line
                  headings (notably the Courses tab). Now the heading gets
                  the full card width so it can breathe on one or two
                  balanced lines. */}
              <div>
                <h3 className="font-sans text-[20px] font-semibold leading-tight tracking-tight text-white sm:text-[22px] md:text-[26px] lg:text-[28px]">
                  {active.title}
                </h3>
                <p className="mt-3 font-inter text-[14px] leading-relaxed text-white/70 md:text-[15px]">
                  {active.description}
                </p>

                {/* CTAs or Coming Soon pill. The pill matches the primary
                    button geometry defined in globals.css:
                    height 48px, 24px horizontal padding, 41px radius,
                    Inter 500 / 16px. Keeping it on the same pill size as
                    "Start Free Trial" prevents a visual regression
                    when switching between tabs. */}
                <div className="mt-6">
                  {active.comingSoon ? (
                    <span
                      className="inline-flex items-center justify-center rounded-[41px] bg-white/10 font-inter font-medium text-white/80"
                      style={{
                        height: 48,
                        paddingLeft: 24,
                        paddingRight: 24,
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      Coming soon
                    </span>
                  ) : (
                    <CtaButtonGroup
                      ctas={[
                        { ...primaryCta, text: active.ctaLabels.primary },
                        {
                          ...secondaryCta,
                          text: active.ctaLabels.secondary,
                          ...(active.learnMoreHref
                            ? { actionType: CtaActionType.DelayedHref, href: active.learnMoreHref }
                            : {}),
                        },
                      ]}
                      gap="flex gap-3"
                      fullWidthOnMobile={false}
                    />
                  )}
                </div>
              </div>

              {/* Visual area */}
              <div className="mt-2">
                <TabVisual
                  image={active.image}
                  imageMobile={active.imageMobile}
                  alt={active.title}
                  kind={active.visualKind}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PlatformTabsSection;
